import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PageTitle from "../../components/common/PageTitle";
import ViewDocumentDialog from "../../components/product-manager/ViewDocumentDialog";
import { apiService } from "../../api/services/apiService";
import { CmsHttpError } from "../../api/client";
import type { Document, LibraryQueryPlan, LibrarySearchHit } from "../../types";
import { operatorDocumentClassLabel } from "../../utils/operatorDocuments";

const EXAMPLE_QUERIES = [
  "SOP for APIs",
  "Runbook for APP",
  "SOP for design of a API proxy",
  "SOP for Managing a KVM configuration",
];

const TenantKnowledgeSearchPage: React.FC = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [hits, setHits] = useState<LibrarySearchHit[]>([]);
  const [inferred, setInferred] = useState<LibraryQueryPlan | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewDoc, setViewDoc] = useState<Document | null>(null);
  const [searched, setSearched] = useState(false);

  const inferredChips = useMemo(() => {
    if (!inferred) return [];
    const chips: { key: string; label: string }[] = [];
    for (const type of inferred.documentTypes ?? []) {
      chips.push({ key: `type-${type}`, label: `Type: ${operatorDocumentClassLabel(type)}` });
    }
    for (const type of inferred.parentTypes ?? []) {
      chips.push({ key: `parent-${type}`, label: `Parent: ${type}` });
    }
    for (const topic of inferred.topics ?? []) {
      chips.push({ key: `topic-${topic}`, label: `Topic: ${topic}` });
    }
    if (inferred.subjectType) {
      chips.push({ key: "subject", label: `Subject: ${inferred.subjectType}` });
    }
    return chips;
  }, [inferred]);

  const runSearch = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || unavailable) return;
    setQuery(trimmed);
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const response = await apiService.searchLibraryDocuments({ query: trimmed, limit: 8 });
      setHits(response.hits ?? []);
      setInferred(response.inferredQuery ?? null);
    } catch (err: unknown) {
      if (err instanceof CmsHttpError && err.status === 503) {
        setUnavailable(true);
        setHits([]);
        setInferred(null);
        setError("Knowledge search is not enabled for this tenant.");
      } else {
        setError(err instanceof Error ? err.message : "Search failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const openDocument = async (documentId: string) => {
    try {
      const document = await apiService.getDocument(documentId);
      setViewDoc(document);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to open document");
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setError("Unable to copy to clipboard");
    }
  };

  return (
    <>
      <PageTitle
        pageTitle="Tenant Knowledge Search"
        pageDescription="Search SOPs, runbooks, and other library documents for this tenant."
      />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            placeholder="Search this tenant’s indexed library"
            value={query}
            disabled={unavailable}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runSearch(query);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    disabled={unavailable || loading || !query.trim()}
                    onClick={() => void runSearch(query)}
                    sx={{ textTransform: "none" }}
                  >
                    Search
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {EXAMPLE_QUERIES.map((example) => (
              <Chip
                key={example}
                label={example}
                clickable={!unavailable}
                onClick={() => void runSearch(example)}
                variant="outlined"
              />
            ))}
          </Stack>
          {inferredChips.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {inferredChips.map((chip) => (
                <Chip key={chip.key} size="small" color="primary" variant="outlined" label={chip.label} />
              ))}
            </Stack>
          )}
          {unavailable && (
            <Alert severity="info">
              Knowledge search is not enabled for this tenant. Document Hub still manages files.
            </Alert>
          )}
          {error && !unavailable && <Alert severity="error">{error}</Alert>}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && searched && hits.length === 0 && !unavailable && !error && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No matching knowledge in this tenant
            </Typography>
          )}
          <Stack spacing={2}>
            {hits.map((hit) => {
              const expanded = expandedId === hit.documentId;
              return (
                <Card key={hit.documentId} variant="outlined">
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ md: "center" }}
                    >
                      <Box>
                        <Typography variant="h6">{hit.name}</Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          {hit.sectionType && (
                            <Chip size="small" label={operatorDocumentClassLabel(hit.sectionType)} />
                          )}
                          {(hit.parents ?? []).map((parent) => (
                            <Chip
                              key={`${parent.entityType}-${parent.entityId}`}
                              size="small"
                              variant="outlined"
                              label={`${parent.entityType}: ${parent.name || parent.entityId}`}
                            />
                          ))}
                          {hit.score != null && (
                            <Chip size="small" variant="outlined" label={`Score ${hit.score.toFixed(3)}`} />
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          onClick={() => void openDocument(hit.documentId)}
                          sx={{ textTransform: "none" }}
                        >
                          Open document
                        </Button>
                        <Tooltip title={expanded ? "Hide section" : "Show section"}>
                          <IconButton
                            aria-label={expanded ? "Hide section" : "Show section"}
                            onClick={() => setExpandedId(expanded ? null : hit.documentId)}
                          >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    {hit.excerpt && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {hit.excerpt}
                      </Typography>
                    )}
                    <Collapse in={expanded}>
                      <Box sx={{ mt: 2, position: "relative" }}>
                        <Tooltip title="Copy section">
                          <IconButton
                            size="small"
                            sx={{ position: "absolute", top: 0, right: 0 }}
                            aria-label="Copy section"
                            onClick={() => void copyText(hit.content || hit.excerpt || "")}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          component="pre"
                          sx={{
                            whiteSpace: "pre-wrap",
                            fontFamily: "inherit",
                            pr: 5,
                            maxHeight: 360,
                            overflow: "auto",
                          }}
                        >
                          {hit.content || hit.excerpt || ""}
                        </Typography>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      </Container>
      <ViewDocumentDialog
        document={viewDoc}
        open={Boolean(viewDoc)}
        onClose={() => setViewDoc(null)}
      />
    </>
  );
};

export default TenantKnowledgeSearchPage;
